import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/ui/Navbar";
import { toast } from "react-toastify";
import { Card, CardContent } from "../components/ui/card";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  LoaderPinwheel,
  Building2,
  Cpu,
  Users,
  Wrench,
  Search,
} from "lucide-react";
import ModalAsignarSuscripcion from "../components/modals/ModalAsignarSuscripcion";

const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed"];

const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const cargar = () => {
    axios
      .get(`${API}/dashboardGlobal/global`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Error al cargar el dashboard global"));
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEmpresa = async (id) => {
    try {
      const res = await fetch(`${API}/admin/empresas/${id}/toggle`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || "No se pudo actualizar");
      cargar();
    } catch (err) {
      toast.error(err.message || "Error al actualizar la empresa");
    }
  };

  const toggleBusquedaImagenes = async (id) => {
    try {
      const res = await fetch(
        `${API}/admin/empresas/${id}/toggle-busqueda-imagenes`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || "No se pudo actualizar");
      cargar();
    } catch (err) {
      toast.error(err.message || "Error al actualizar la búsqueda de imágenes");
    }
  };

  const empresasFiltradas = useMemo(() => {
    if (!data) return [];
    const q = busqueda.trim().toLowerCase();
    if (!q) return data.empresas;
    return data.empresas.filter(
      (e) =>
        e.razon_social.toLowerCase().includes(q) ||
        e.nit.toLowerCase().includes(q)
    );
  }, [data, busqueda]);

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="h-screen flex items-center justify-center">
          <LoaderPinwheel className="animate-spin w-10 h-10 text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 md:px-6 pt-20 md:pt-28 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="text-center">
              <Building2 className="mx-auto text-blue-600 mb-1" />
              <p className="text-gray-500 text-sm">Empresas</p>
              <p className="text-2xl font-bold text-blue-600">
                {data.indicadores.totalEmpresas}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="text-center">
              <Cpu className="mx-auto text-green-600 mb-1" />
              <p className="text-gray-500 text-sm">Equipos</p>
              <p className="text-2xl font-bold text-green-600">
                {data.indicadores.totalEquipos}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="text-center">
              <Users className="mx-auto text-amber-600 mb-1" />
              <p className="text-gray-500 text-sm">Usuarios</p>
              <p className="text-2xl font-bold text-amber-600">
                {data.indicadores.totalUsuarios}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="text-center">
              <Wrench className="mx-auto text-purple-600 mb-1" />
              <p className="text-gray-500 text-sm">Mantenimientos</p>
              <p className="text-2xl font-bold text-purple-600">
                {data.indicadores.totalGestiones}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">Empresas por suscripción</h2>
            <ResponsiveContainer height={260}>
              <PieChart>
                <Pie
                  data={data.empresasPorSuscripcion}
                  dataKey="total"
                  nameKey="_id"
                  outerRadius={90}
                >
                  {data.empresasPorSuscripcion.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por razón social o NIT..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {empresasFiltradas.map((e) => (
            <Card key={e._id} className="hover:shadow-md transition">
              <CardContent>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {e.razon_social}
                    </h3>
                    <p className="text-xs text-gray-500">NIT {e.nit}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        e.activa
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {e.activa ? "Activa" : "Inactiva"}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {e.plan}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center my-3">
                  <div>
                    <p className="text-lg font-bold text-gray-800">
                      {e.equipos}
                    </p>
                    <p className="text-[11px] text-gray-500">Equipos</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">
                      {e.usuarios}
                    </p>
                    <p className="text-[11px] text-gray-500">Usuarios</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">
                      {e.cronogramas}
                    </p>
                    <p className="text-[11px] text-gray-500">Cronogramas</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">
                      {e.gestiones}
                    </p>
                    <p className="text-[11px] text-gray-500">Mantenim.</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    className="flex-1 bg-gray-800 hover:bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg"
                    onClick={() => navigate(`/adminDashboard/empresa/${e._id}`)}
                  >
                    Ver detalle
                  </button>
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded-lg"
                    onClick={() => toggleEmpresa(e._id)}
                  >
                    {e.activa ? "Desactivar" : "Activar"}
                  </button>
                  <button
                    className={`text-sm px-3 py-1.5 rounded-lg text-white ${
                      e.busquedaImagenesIA
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "bg-gray-400 hover:bg-gray-500"
                    }`}
                    onClick={() => toggleBusquedaImagenes(e._id)}
                    title="Búsqueda web de imágenes de producto vía IA (consume tokens por modelo nuevo)"
                  >
                    {e.busquedaImagenesIA ? "Imágenes IA: ON" : "Imágenes IA: OFF"}
                  </button>
                  <button
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm px-3 py-1.5 rounded-lg"
                    onClick={() => {
                      setEmpresaSeleccionada(e);
                      setOpenModal(true);
                    }}
                  >
                    Cambiar plan
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}

          {empresasFiltradas.length === 0 && (
            <p className="text-gray-500 text-sm col-span-full text-center py-6">
              No se encontraron empresas.
            </p>
          )}
        </div>
      </div>

      <ModalAsignarSuscripcion
        open={openModal}
        onClose={() => setOpenModal(false)}
        empresa={empresaSeleccionada}
        onUpdated={cargar}
      />
    </div>
  );
};

export default AdminDashboardPage;
